import React, { useState, useRef } from 'react';

/**
 * Inline OTP entry shown in the chat when a returning user's email is recognized.
 * The user types the 6-digit code we emailed; on success the widget switches into
 * returning-user mode (profile pre-loaded).
 */
const LABELS = {
  en: { title: 'Enter your verification code', sub: "We emailed a 6-digit code to", verify: 'Verify', resend: 'Resend code', skip: 'Start fresh instead', invalid: 'Invalid or expired code. Please try again.' },
  he: { title: 'הזינו את קוד האימות', sub: 'שלחנו קוד בן 6 ספרות אל', verify: 'אימות', resend: 'שליחה מחדש', skip: 'להתחיל מחדש', invalid: 'קוד שגוי או שפג תוקפו. נסו שוב.' },
  es: { title: 'Ingresa tu código de verificación', sub: 'Enviamos un código de 6 dígitos a', verify: 'Verificar', resend: 'Reenviar código', skip: 'Empezar de nuevo', invalid: 'Código inválido o expirado. Inténtalo de nuevo.' },
  fr: { title: 'Entrez votre code de vérification', sub: 'Nous avons envoyé un code à 6 chiffres à', verify: 'Vérifier', resend: 'Renvoyer le code', skip: 'Recommencer', invalid: 'Code invalide ou expiré. Réessayez.' },
  pt: { title: 'Digite seu código de verificação', sub: 'Enviamos um código de 6 dígitos para', verify: 'Verificar', resend: 'Reenviar código', skip: 'Começar de novo', invalid: 'Código inválido ou expirado. Tente novamente.' },
  ar: { title: 'أدخل رمز التحقق', sub: 'أرسلنا رمزًا من 6 أرقام إلى', verify: 'تحقق', resend: 'إعادة إرسال الرمز', skip: 'ابدأ من جديد', invalid: 'رمز غير صالح أو منتهي الصلاحية. حاول مرة أخرى.' },
};

const InlineOtpInput = ({ maskedEmail, onSubmit, onResend, onSkip, error, submitting, darkMode, language }) => {
  const [code, setCode] = useState('');
  const inputRef = useRef(null);
  const t = LABELS[language] || LABELS.en;

  const submit = () => {
    if (code.trim().length >= 4 && !submitting) onSubmit(code.trim());
  };

  const cardBg = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)';
  const textColor = darkMode ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.75)';
  const subColor = darkMode ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.5)';

  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 16px' }}>
      <div style={{
        background: cardBg, borderRadius: 14, padding: '18px 20px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
        maxWidth: 340, width: '100%'
      }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: textColor }}>{t.title}</span>
        <span style={{ fontSize: 12.5, color: subColor, textAlign: 'center' }}>
          {t.sub} <strong>{maskedEmail}</strong>
        </span>
        <input
          ref={inputRef}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="••••••"
          style={{
            width: 160, textAlign: 'center', letterSpacing: 8, fontSize: 22,
            padding: '10px 12px', borderRadius: 10,
            border: `1px solid ${error ? '#ef4444' : (darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)')}`,
            background: darkMode ? 'rgba(0,0,0,0.2)' : '#fff',
            color: textColor, outline: 'none'
          }}
        />
        {error && <span style={{ fontSize: 12, color: '#ef4444' }}>{t.invalid}</span>}
        <button
          onClick={submit}
          disabled={code.trim().length < 4 || submitting}
          style={{
            width: '100%', padding: '11px 0', borderRadius: 10, border: 'none',
            background: code.trim().length >= 4 && !submitting ? '#7c3aed' : 'rgba(124,58,237,0.4)',
            color: '#fff', fontWeight: 600, fontSize: 15,
            cursor: code.trim().length >= 4 && !submitting ? 'pointer' : 'default'
          }}
        >
          {submitting ? '…' : t.verify}
        </button>
        <div style={{ display: 'flex', gap: 16, marginTop: 2 }}>
          <button onClick={onResend} disabled={submitting} style={linkBtn(subColor)}>{t.resend}</button>
          <button onClick={onSkip} disabled={submitting} style={linkBtn(subColor)}>{t.skip}</button>
        </div>
      </div>
    </div>
  );
};

const linkBtn = (color) => ({
  background: 'none', border: 'none', color, fontSize: 12.5,
  textDecoration: 'underline', cursor: 'pointer', padding: 0
});

export default InlineOtpInput;
